import { Component, OnInit } from '@angular/core';
import { EmployeeService } from '../../../../core/services/employee.service';
import { BaseComponent } from '../../../../core/abstracts/baseComponent';
import { EmployeeExclusionReportMatchModel, EmployeeExclusionReportUploadModel } from '../../../../models/employeeExclusionReportModel';
import { Store } from '@ngrx/store';
import { State } from '../../../../core/store';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-exclusion-reports',
  templateUrl: './exclusion-reports.component.html',
  styleUrls: ['./exclusion-reports.component.scss'],
  standalone: false
})
export class ExclusionReportsComponent extends BaseComponent implements OnInit {
  readonly completedStatus = 'Completed';
  readonly failedStatus = 'Failed';
  readonly processingStatuses = ['Queued', 'Processing'];
  readonly reports = [
    { label: 'LEIE Database', value: 'LEIE' },
    { label: 'SAMS Database', value: 'SAMS' }
  ];

  reportType = 'LEIE';
  uploads: EmployeeExclusionReportUploadModel[] = [];
  matches: EmployeeExclusionReportMatchModel[] = [];
  selectedUpload: EmployeeExclusionReportUploadModel;
  selectedFile: File;
  startDate: string;
  endDate: string;
  userSearch = '';
  loadingUploads = false;
  loadingMatches = false;
  uploading = false;
  errorMessage: string;
  successMessage: string;
  canViewEmployeePayrollReports = false;
  private processingRefreshTimeout;

  constructor(
    private employeeService: EmployeeService,
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<State>) {
    super();
  }

  ngOnInit() {
    this.subs.push(this.store.select(s => s.accountState.user?.permissionIDs).subscribe(permissionIDs => {
      this.canViewEmployeePayrollReports = permissionIDs && !!permissionIDs.find(p => p === 3040);
    }));
    this.subs.push(this.route.queryParamMap.subscribe(params => {
      const reportType = (params.get('reportType') || '').toUpperCase();
      if (this.isValidReportType(reportType) && this.reportType !== reportType) {
        this.reportType = reportType;
        this.selectedUpload = null;
        this.matches = [];
        this.selectedFile = null;
        this.clearMessages();
      }

      this.loadUploads();
    }));
  }

  ngOnDestroy() {
    this.clearProcessingRefresh();
    super.ngOnDestroy();
  }

  selectReport(reportType: string) {
    if (this.reportType === reportType) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { reportType }
    });
  }

  onFileSelected(event) {
    this.selectedFile = event?.target?.files?.length ? event.target.files[0] : null;
    this.clearMessages();
  }

  uploadReport() {
    if (!this.selectedFile) {
      this.errorMessage = 'Select a CSV or Excel file before uploading.';
      return;
    }

    this.uploading = true;
    this.clearMessages();
    this.subs.push(this.employeeService.uploadEmployeeExclusionReport(this.reportType, this.selectedFile).subscribe({
      next: upload => {
        this.uploading = false;
        this.successMessage = 'Report uploaded and processing.';
        this.selectedFile = null;
        this.loadUploads(upload.id);
      },
      error: error => {
        this.uploading = false;
        this.errorMessage = error?.error || 'The report could not be uploaded.';
      }
    }));
  }

  loadUploads(selectUploadId: number = null) {
    this.loadingUploads = true;
    this.subs.push(this.employeeService.getEmployeeExclusionReportUploads(this.reportType, this.startDate, this.endDate, this.userSearch).subscribe({
      next: uploads => {
        this.loadingUploads = false;
        this.uploads = uploads || [];
        if (selectUploadId) {
          const upload = this.uploads.find(x => x.id === selectUploadId);
          if (upload) {
            if (this.isUploadComplete(upload)) {
              this.viewMatches(upload);
            } else {
              this.selectedUpload = upload;
              this.matches = [];
            }
          }
        }
        this.scheduleProcessingRefresh();
      },
      error: error => {
        this.loadingUploads = false;
        this.errorMessage = error?.error || 'Report uploads could not be loaded.';
      }
    }));
  }

  viewMatches(upload: EmployeeExclusionReportUploadModel) {
    this.selectedUpload = upload;
    this.loadingMatches = true;
    this.matches = [];
    this.clearMessages();

    if (!this.isUploadComplete(upload)) {
      this.loadingMatches = false;
      if (this.isUploadFailed(upload)) {
        this.errorMessage = upload.processingError || 'Report processing failed.';
      }
      return;
    }

    this.subs.push(this.employeeService.getEmployeeExclusionReportMatches(upload.id).subscribe({
      next: matches => {
        this.loadingMatches = false;
        this.matches = matches || [];
      },
      error: error => {
        this.loadingMatches = false;
        this.errorMessage = error?.error || 'Match results could not be loaded.';
      }
    }));
  }

  clearFilters() {
    this.startDate = null;
    this.endDate = null;
    this.userSearch = '';
    this.loadUploads();
  }

  isUploadComplete(upload: EmployeeExclusionReportUploadModel) {
    return (upload?.processingStatus || this.completedStatus) === this.completedStatus;
  }

  isUploadFailed(upload: EmployeeExclusionReportUploadModel) {
    return upload?.processingStatus === this.failedStatus;
  }

  isUploadProcessing(upload: EmployeeExclusionReportUploadModel) {
    return this.processingStatuses.indexOf(upload?.processingStatus) >= 0;
  }

  private scheduleProcessingRefresh() {
    this.clearProcessingRefresh();
    if (this.uploads && this.uploads.some(upload => this.isUploadProcessing(upload))) {
      this.processingRefreshTimeout = setTimeout(() => this.loadUploads(this.selectedUpload?.id), 10000);
    }
  }

  private clearProcessingRefresh() {
    if (this.processingRefreshTimeout) {
      clearTimeout(this.processingRefreshTimeout);
      this.processingRefreshTimeout = null;
    }
  }

  private clearMessages() {
    this.errorMessage = null;
    this.successMessage = null;
  }

  private isValidReportType(reportType: string) {
    return this.reports.some(report => report.value === reportType);
  }
}
